import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { theme } from '@omnigenbiodata/ui';
import { Participant } from '../../../../../../core/types/common.types';
import DetailRow from '../DetailRow';
import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';

const successTint = theme.palette.success.lighter;
const successMain = theme.palette.success.main;
const warningTint = theme.palette.warning.lighter;
const warningMain = theme.palette.warning.main;
const errorTint = theme.palette.error.lighter;
const errorMain = theme.palette.error.main;

interface ParticipantConfirmationPanelProps {
  participant: Participant;
  onConfirm: () => void;
}

function ParticipantConfirmationPanel({ participant, onConfirm }: ParticipantConfirmationPanelProps) {
  const invalidParticipantStatuses = ['WITHDRAWN', 'SUSPENDED', 'INELIGIBLE'];
  const hasInvalidStatus = invalidParticipantStatuses.includes(participant.participantStatus);
  const hasExistingSample = Boolean(participant.sampleBarcode);

  const canConfirm = !hasInvalidStatus && !hasExistingSample;

  const panelTint = hasInvalidStatus ? errorTint : hasExistingSample ? warningTint : successTint;
  const panelMain = hasInvalidStatus ? errorMain : hasExistingSample ? warningMain : successMain;

  const title = hasInvalidStatus
    ? `Participant ${participant.participantStatus}`
    : hasExistingSample
      ? 'Sample Already Linked'
      : 'Confirm Participant';

  const description = hasInvalidStatus
    ? `This participant has a status of ${participant.participantStatus} and cannot be linked to a new sample kit.`
    : hasExistingSample
      ? 'This participant already has a sample kit linked. Please do not continue with a new sample link unless the existing record has been reviewed.'
      : 'Please confirm this is the correct participant before continuing.';

  const icon = hasInvalidStatus ? (
    <BlockOutlined color="error" sx={{ fontSize: 30 }} />
  ) : hasExistingSample ? (
    <Inventory2Outlined color="warning" sx={{ fontSize: 30 }} />
  ) : (
    <PersonSearchOutlined color="success" sx={{ fontSize: 30 }} />
  );

  return (
    <Paper
      elevation={4}
      sx={{
        mt: 4,
        border: 1,
        borderColor: panelMain,
        bgcolor: panelTint,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 8, bgcolor: panelMain }} />

      <Box p={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {title}
            </Typography>

            <Typography variant="body1" component="p" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>

        <Box component="dl" sx={{ m: 0 }}>
          <DetailRow label="Participant Ref" value={participant.participantRef} />
          <DetailRow label="Status" value={participant.participantStatus} />
          <DetailRow label="First Name" value={participant.firstName} />
          <DetailRow label="Surname" value={participant.surname} />
          <DetailRow label="DOB" value={participant.dob} />
          <DetailRow label="Sex at Birth" value={participant.sexAtBirth} />
          <DetailRow label="NHS Number" value={participant.nhsNumber} />

          {participant.sampleBarcode && <DetailRow label="Existing Sample Barcode" value={participant.sampleBarcode} />}
        </Box>

        {!canConfirm && (
          <Box
            sx={{
              mt: 3,
              p: 2.5,
              borderRadius: 3,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: panelMain,
            }}
          >
            <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Cannot continue
            </Typography>

            <Typography variant="body1" component="p" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {hasInvalidStatus
                ? `The participant record indicates that the participant status is ${participant.participantStatus}. A sample kit must not be linked to this participant.`
                : 'The participant record already contains a linked sample barcode. Please check the existing sample record before taking any further action.'}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={<CheckCircleOutlined />}
          onClick={onConfirm}
          disabled={!canConfirm}
          sx={{
            minHeight: 64,
            fontSize: '1.15rem',
            fontWeight: 700,
          }}
        >
          {canConfirm ? 'Confirm this is the correct participant' : 'Participant cannot be confirmed'}
        </Button>
      </Box>
    </Paper>
  );
}

export default ParticipantConfirmationPanel;
